import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from '../base/Button';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showProgramsMenu, setShowProgramsMenu] = useState(false);
  const [showAdmissionsMenu, setShowAdmissionsMenu] = useState(false);
  const [animatedText, setAnimatedText] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  const [admissionsOpen, setAdmissionsOpen] = useState(false);
  const location = useLocation();

  const fullText = 'Creative Journeys Learning Center';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animate text on route change
  useEffect(() => {
    setAnimatedText('');
    let currentIndex = 0;
    
    const animateText = () => {
      if (currentIndex <= fullText.length) {
        setAnimatedText(fullText.slice(0, currentIndex));
        currentIndex++;
        setTimeout(animateText, 80);
      }
    };
    
    // Start animation after a small delay
    const timer = setTimeout(animateText, 200);
    
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { 
      name: 'Programs', 
      path: '/programs',
      hasDropdown: true,
      submenu: [
        { name: 'Infants', path: '/programs/infants' },
        { name: 'Toddlers', path: '/programs/toddlers' },
        { name: 'Preschool', path: '/programs/preschool' },
        { name: 'Pre-K', path: '/programs/pre-k' },
        { name: 'Curriculum', path: '/programs/curriculum' },
        { name: 'Gallery', path: '/gallery' }
      ]
    },
    { 
      name: 'Admissions', 
      path: '/admissions',
      hasDropdown: true,
      submenu: [
        { name: 'Tuition & Fees', path: '/admissions/tuition' },
        { name: 'How to Enroll', path: '/admissions/enroll' },
        { name: 'Forms & Downloads', path: '/admissions/forms' }
      ]
    },
    { name: 'Contact & Tours', path: '/contact' }
  ];

  return (
    <header 
      className={`fixed top-0 left-right w-full z-50 bg-[#FFF9F1] transition-all duration-250 ease-out ${
        isScrolled ? 'shadow-[0_4px_14px_rgba(0,0,0,0.06)]' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Animated Logo */}
          <Link to="/" className="flex items-center justify-center md:justify-start flex-1 md:flex-none">
            <div className="text-2xl font-bold text-[#2E5939] min-w-[400px] text-center md:text-left" style={{ fontFamily: '"Fredoka", sans-serif' }}>
              {/* Show animated text on desktop only */}
              <span className="hidden md:inline-block">
                {animatedText}
              </span>
            </div>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex space-x-8">
            <a href="/" className="text-gray-700 hover:text-teal-600 transition-colors">
              Home
            </a>
            <a href="/about" className="text-gray-700 hover:text-teal-600 transition-colors">
              About Us
            </a>
            <div 
              className="relative group"
              onMouseEnter={() => setShowProgramsMenu(true)}
              onMouseLeave={() => setShowProgramsMenu(false)}
            >
              <a href="/programs" className="text-gray-700 hover:text-teal-600 transition-colors flex items-center">
                Programs
                <i className="ri-arrow-down-s-line ml-1"></i>
              </a>
              <div className={`absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg transition-all duration-200 z-50 ${
                showProgramsMenu ? 'opacity-100 visible' : 'opacity-0 invisible'
              }`}>
                <div className="py-2">
                  <a href="/programs" className="block px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors">
                    All Programs
                  </a>
                  <a href="/programs/curriculum" className="block px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors">
                    Curriculum
                  </a>
                </div>
              </div>
            </div>
            <div 
              className="relative group"
              onMouseEnter={() => setShowAdmissionsMenu(true)}
              onMouseLeave={() => setShowAdmissionsMenu(false)}
            >
              <a href="/admissions" className="text-gray-700 hover:text-teal-600 transition-colors flex items-center">
                Admissions
                <i className="ri-arrow-down-s-line ml-1"></i>
              </a>
              <div className={`absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg transition-all duration-200 z-50 ${
                showAdmissionsMenu ? 'opacity-100 visible' : 'opacity-0 invisible'
              }`}>
                <div className="py-2">
                  <a href="/admissions" className="block px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors">
                    Overview
                  </a>
                  <a href="/admissions/enroll" className="block px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors">
                    How to Enroll
                  </a>
                  <a href="/admissions/tuition" className="block px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors">
                    Tuition & Fees
                  </a>
                  <a href="/admissions/forms" className="block px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors">
                    Forms & Downloads
                  </a>
                </div>
              </div>
            </div>
            <a href="/contact" className="text-gray-700 hover:text-teal-600 transition-colors">
              Contact & Tours
            </a>
          </nav>

          {/* CTA Button */}
          <div className="flex items-center space-x-3">
            <a 
              href="https://www.youtube.com/@creativejourneyschildcarel5091"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                variant="primary" 
                className="rounded-full"
              >
                Schedule a Tour
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-teal-600 transition-colors"
            >
              <i className={`text-2xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-2 space-y-2">
            <a href="/" className="block py-2 text-gray-700 hover:text-teal-600 transition-colors">
              Home
            </a>
            <a href="/about" className="block py-2 text-gray-700 hover:text-teal-600 transition-colors">
              About Us
            </a>
            <div>
              <button
                onClick={() => setProgramsOpen(!programsOpen)}
                className="flex items-center justify-between w-full py-2 text-gray-700 hover:text-teal-600 transition-colors"
              >
                Programs
                <i className={`ri-arrow-${programsOpen ? 'up' : 'down'}-s-line`}></i>
              </button>
              {programsOpen && (
                <div className="pl-4 space-y-1">
                  <a href="/programs" className="block py-1 text-gray-600 hover:text-teal-600 transition-colors">
                    All Programs
                  </a>
                  <a href="/programs/curriculum" className="block py-1 text-gray-600 hover:text-teal-600 transition-colors">
                    Curriculum
                  </a>
                </div>
              )}
            </div>
            <div>
              <button
                onClick={() => setAdmissionsOpen(!admissionsOpen)}
                className="flex items-center justify-between w-full py-2 text-gray-700 hover:text-teal-600 transition-colors"
              >
                Admissions
                <i className={`ri-arrow-${admissionsOpen ? 'up' : 'down'}-s-line`}></i>
              </button>
              {admissionsOpen && (
                <div className="pl-4 space-y-1">
                  <a href="/admissions" className="block py-1 text-gray-600 hover:text-teal-600 transition-colors">
                    Overview
                  </a>
                  <a href="/admissions/enroll" className="block py-1 text-gray-600 hover:text-teal-600 transition-colors">
                    How to Enroll
                  </a>
                  <a href="/admissions/tuition" className="block py-1 text-gray-600 hover:text-teal-600 transition-colors">
                    Tuition & Fees
                  </a>
                  <a href="/admissions/forms" className="block py-1 text-gray-600 hover:text-teal-600 transition-colors">
                    Forms & Downloads
                  </a>
                </div>
              )}
            </div>
            <a href="/contact" className="block py-2 text-gray-700 hover:text-teal-600 transition-colors">
              Contact & Tours
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
